import { useLocation, useNavigate } from 'react-router';
import { newZonePath, zoneRecordPath, zoneViewPath, type ZoneView } from '../zones.paths';

/** Carried on the way into a zone, so closing it returns to exactly where it was opened from (the table's page included). */
interface ZoneLocationState {
  from?: string;
}

/**
 * Moving around the Zones section from `view`. A zone opens in the view it was opened from (beside the map, or
 * in the table's sheet), and the search and filters stay in the URL throughout, so the map around an open zone
 * shows the same zones the table or map did.
 */
export function useZoneNavigation(view: ZoneView) {
  const navigate = useNavigate();
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  const from = (location.state as ZoneLocationState | null)?.from;

  const intoZone = { from: from ?? here };

  return {
    /** For a link to a zone (the table's names): the same target and state `openZone` navigates with. */
    zoneLink: (zoneId: string) => ({ to: `${zoneRecordPath(view, zoneId)}${location.search}`, state: intoZone }),
    openZone: (zoneId: string) => navigate(`${zoneRecordPath(view, zoneId)}${location.search}`, { state: intoZone }),
    openNewZone: () => navigate(`${newZonePath(view)}${location.search}`, { state: intoZone }),
    closeZone: () => navigate(from ?? `${zoneViewPath(view)}${location.search}`),
    switchView: (next: ZoneView) => navigate(`${zoneViewPath(next)}${location.search}`),
  };
}
